import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LogOut, Settings, Search, Menu, ChevronDown } from 'lucide-react';
import { UserRole, AGENCY_INFO } from '@/types/rbac.types';
import { SearchModal } from '@/components/search/search-modal';
import { useState } from 'react';
// import { NotificationDropdown } from '@/components/notifications/notification-dropdown';
import { Kbd } from '@/components/ui/kbd';

interface HeaderProps {
    onMenuClick?: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export const Header = ({ onMenuClick }: HeaderProps) => {
    const { user, logout } = useAuthStore();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const agency = AGENCY_INFO[user?.role ?? UserRole.DVLA];
    const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';

    return (
        <header className="sticky top-0 z-20 h-16 w-full shrink-0 border-b bg-card/85 backdrop-blur-xl backdrop-saturate-150">
            <div className="flex h-full items-center gap-3 px-4 md:px-6 lg:px-8">
                <button
                    onClick={onMenuClick}
                    className="-ml-1 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:hidden"
                    aria-label="Open menu"
                >
                    <Menu className="h-5 w-5" />
                </button>

                {/* Global search trigger */}
                <button
                    onClick={() => setIsSearchOpen(true)}
                    className="group flex h-10 flex-1 items-center gap-2.5 rounded-lg border border-input bg-subtle px-3 text-left text-body-sm text-muted-foreground transition-colors hover:border-foreground/25 hover:bg-card focus-visible:outline-none focus-visible:shadow-focus md:max-w-md"
                    aria-label="Open search"
                >
                    <Search className="h-4 w-4 shrink-0" />
                    <span className="truncate">Search VIN, plate or case ID…</span>
                    <span className="ml-auto hidden items-center gap-1 sm:flex">
                        <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
                        <Kbd>K</Kbd>
                    </span>
                </button>
                <SearchModal open={isSearchOpen} setOpen={setIsSearchOpen} />

                <div className="ml-auto flex items-center gap-1 sm:gap-2">
                    {/* Notification bar commented out for now */}
                    {/* <NotificationDropdown /> */}
                    {/* <div className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden /> */}

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-2.5 rounded-lg p-1 pr-2 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:shadow-focus" aria-label="Account menu">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-subtle text-caption font-bold text-primary-subtle-foreground">
                                    {initials}
                                </span>
                                <span className="hidden flex-col items-start leading-tight sm:flex">
                                    <span className="text-body-sm font-semibold text-foreground">{user?.firstName} {user?.lastName}</span>
                                    <span className="text-caption text-muted-foreground">{agency.short}</span>
                                </span>
                                <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
                            </button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent className="w-64" align="end">
                            <div className="px-2.5 py-2">
                                <p className="truncate text-body-sm font-semibold text-foreground">{user?.firstName} {user?.lastName}</p>
                                <p className="truncate text-caption text-muted-foreground">{user?.email}</p>
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild className="cursor-pointer">
                                <Link to="/profile">
                                    <Settings className="text-muted-foreground" />
                                    Account settings
                                </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={logout} className="cursor-pointer text-danger focus:bg-danger-subtle focus:text-danger">
                                <LogOut />
                                Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
};
